import { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  isInactiveRepository,
  getRepositoriesFromProvider,
  getActiveRepositories
} from './repositories-modal.service';
import {
  getNumOfSelectableRepos,
  isInOnboardingPath
} from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import {
  REPOSITORY_ERRORS,
  REPOSITORY_OWNER_TYPES,
  TRACKING
} from 'common/constants/constants';

const useRepositoriesModal = ({
  sensor_authorization_id,
  repositories,
  license,
  validateRepos,
  createRepositories = _.noop,
  onSubmitCb = _.noop,
  onRemoveMessage = _.noop,
  backfillType,
  newRepositories = [],
  providerConfig,
  account,
  sensor_name,
  isPat
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [numOfSelectableRepos, setNumOfSelectableRepos] = useState(5);
  const [stateRepositories, setStateRepositories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [term, setTerm] = useState('');
  const [error, setError] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const isOnboarding = isInOnboardingPath();
  const [ownerRepositories, setOwnerRepositories] = useState([]);
  const [openedOwnerRepositories, setOpenedOwnerRepositories] = useState('');
  const [multipleNew, setMultipleNew] = useState(false);
  const activeRepos = getActiveRepositories(stateRepositories);
  const toggleRepositorySelection = repositoryId => {
    if (selected.has(repositoryId)) {
      selected.delete(repositoryId);
    } else {
      selected.add(repositoryId);
    }

    setSelected(new Set(selected));
  };

  const isCheckboxDisabled = repositoryId =>
    !selected.has(repositoryId) && selected.size >= numOfSelectableRepos;

  const fetchRepositories = async () => {
    const response = await getRepositoriesFromProvider({
      sensor_authorization_id,
      repositories,
      isOnboarding,
      providerConfig
    });

    const newRepositoriesHttpUrl = newRepositories.map(
      ({ http_url }) => http_url
    );

    const selectableRepositoriesWithNew = response.items.map(repo => {
      const isNew = newRepositoriesHttpUrl.includes(repo.http_url);
      return {
        ...repo,
        isNew
      };
    });

    const newRepositoriesIds = selectableRepositoriesWithNew
      .filter(({ isNew }) => isNew)
      .map(({ id }) => id);

    if (!isOnboarding) {
      setSelected(new Set(newRepositoriesIds));
    }

    setError(response.error);
    setStateRepositories(selectableRepositoriesWithNew);
    setLoading(false);
  };

  useEffect(() => {
    const hasNoPrivateRepos = repositories && !repositories.length;
    const hasError = error || hasNoPrivateRepos || !activeRepos.length;
    if (isOnboarding && hasError) {
      const errorReason = error
        ? error
        : hasNoPrivateRepos
        ? REPOSITORY_ERRORS.NO_PRIVATE_REPOS
        : REPOSITORY_ERRORS.NO_ACTIVE_REPOS;
      TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_SCAN_REPOS_FAIL, {
        identity_email: account?.email,
        git_provider: sensor_name,
        auth: isPat ? 'pat' : 'oauth',
        reason: errorReason
      });
    }
  }, [repositories, error]);

  useEffect(() => {
    const numOfSelectableRepos = getNumOfSelectableRepos({
      license
    });
    const shouldFetchRepositories = numOfSelectableRepos > 0;
    setNumOfSelectableRepos(numOfSelectableRepos);
    setLoading(shouldFetchRepositories);
    if (shouldFetchRepositories) {
      fetchRepositories();
    } else {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: isOnboarding
          ? TRACKING.PAGES.ONBOARDING
          : TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.SELECT_REPOS
      });
    }
  }, []);

  useEffect(() => {
    const personalRepos = stateRepositories.filter(
      ({ owner_type }) => owner_type === REPOSITORY_OWNER_TYPES.USER
    );
    const reposWithoutType = stateRepositories.filter(
      ({ owner_type }) => !owner_type
    );
    const groupedByRepositories = _(stateRepositories)
      .filter(({ owner_type }) => owner_type === REPOSITORY_OWNER_TYPES.ORG)
      .groupBy(({ owner_login }) => owner_login)
      .entries()
      .sortBy(
        owner =>
          owner[1].filter(repo => !isInactiveRepository(repo.updated_at)).length
      )
      .reverse()
      .fromPairs()
      .assign(
        isOnboarding || personalRepos.length === 0
          ? {}
          : {
              Personal: personalRepos
            }
      )
      .assign(
        reposWithoutType.length === 0
          ? {}
          : {
              Organization: reposWithoutType
            }
      )
      .value();

    const multipleNew =
      new Set(
        stateRepositories
          .filter(repo => repo.isNew)
          .map(({ owner_login }) => owner_login)
      ).size > 1;

    setOwnerRepositories(groupedByRepositories);
    setOpenedOwnerRepositories(
      (!multipleNew && Object.keys(groupedByRepositories)[0]) || ''
    );
    setMultipleNew(multipleNew);
  }, [stateRepositories, term]);

  const onSubmit = () =>
    validateRepos
      ? Array.from(selected)
          .map(id => _.keyBy(stateRepositories, 'id')[id].updated_at)
          .filter(updatedAt => !isInactiveRepository(updatedAt)).length
        ? submit()
        : setIsConfirmationDialogOpen(true)
      : submit();

  const submit = async () => {
    const available_repositories = stateRepositories;
    const repositoriesById = _.keyBy(stateRepositories, 'id');
    const checkedRepositories = Array.from(selected).map(id => ({
      provider_id: id,
      name: repositoriesById[id].name,
      http_url: repositoriesById[id].http_url,
      ...(sensor_authorization_id ? { sensor_authorization_id } : {})
    }));
    setSaving(true);
    const repositories = await createRepositories(
      checkedRepositories,
      backfillType
    );
    await onSubmitCb({ repositories, available_repositories });
    if (isOnboarding) {
      TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_SCAN_REPOS_OK, {
        identity_email: account?.email,
        git_provider: sensor_name,
        auth: isPat ? 'pat' : 'oauth',
        repos: checkedRepositories.length
      });
    }
    await onRemoveMessage();
    setSaving(false);
  };

  return {
    toggleRepositorySelection,
    isCheckboxDisabled,
    getNumOfSelectableRepos: () => getNumOfSelectableRepos({ license }),
    onSubmit,
    submit,
    setTerm,
    setIsConfirmationDialogOpen,
    setOpenedOwnerRepositories,
    setSelected,
    ownerRepositories,
    loading,
    saving,
    error,
    isConfirmationDialogOpen,
    openedOwnerRepositories,
    numOfSelectableRepos,
    selected,
    stateRepositories,
    term,
    isOnboarding,
    multipleNew,
    activeRepos
  };
};

export default useRepositoriesModal;
